import React, { useEffect, useMemo, useState } from 'react';
import api, { extrairMensagemErro } from '../services/api';
import { obterPerfilSessao, perfilPode } from '../permissions';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import { dialog } from '../services/dialog';

type ItemRequisicao = {
  nome?: string;
  descricao_tecnica?: string;
  especificacao?: string;
  quantidade?: number | string;
  unidade?: string;
};

type RequisicaoAprovacao = {
  id: string;
  numero_requisicao: string;
  titulo: string;
  descricao: string;
  departamento?: string;
  itens?: ItemRequisicao[];
  moeda?: string;
  status: string;
  status_aprovacao: string;
  data_criacao?: string;
  data_necessidade?: string;
  data_vencimento?: string;
  criador?: { nome: string; email: string; perfil?: string };
  pode_decidir?: boolean;
  estado_operacional?: string;
};

type HistoricoAprovacao = {
  id: string;
  status: string;
  comentarios?: string | null;
  nivel_alcada?: number;
  valor_limite?: number;
  data_criacao?: string;
  data_decisao?: string;
  aprovador?: { nome: string; email: string; perfil: string };
};

type HistoricoOperacional = {
  id: string;
  acao: string;
  valores_antes?: string | null;
  valores_depois?: string | null;
  data_criacao?: string;
  usuario?: { nome: string; email: string; perfil: string };
};

type DetalheAprovacao = {
  requisicao: RequisicaoAprovacao;
  historico_aprovacoes: HistoricoAprovacao[];
  historico_operacional: HistoricoOperacional[];
  rejeicoes_anteriores: HistoricoAprovacao[];
};

type DecisaoRealizada = {
  id: string;
  requisicao_id: string;
  numero_requisicao: string;
  titulo: string;
  departamento?: string;
  decisao: string;
  comentario?: string | null;
  data_hora?: string;
  estado_atual_processo: string;
  estado_atual_aprovacao: string;
};

type DashboardAprovador = {
  pendentes_decisao?: number;
  pendentes_minha_alcada: number;
  aprovadas_por_mim: number;
  rejeitadas_por_mim: number;
  tempo_medio_decisao_horas: number;
  pendencias_proximas_data_necessidade: number;
  pendencias_vencidas: number;
};

const labelsStatusRequisicao: Record<string, string> = {
  SOLICITADO: 'Solicitado',
  EM_COTACAO: 'Em Cotação',
  PROPOSTA_SELECIONADA: 'Proposta Selecionada',
  AGUARDANDO_COMPROVATIVO: 'Aguardando Comprovativo',
  COMPRA_REALIZADA: 'Compra Realizada',
  EM_TRANSITO: 'Em Trânsito',
  CONCLUIDO: 'Concluído'
};

const labelsStatusAprovacao: Record<string, string> = {
  PENDENTE: 'Pendente',
  APROVADA: 'Aprovada',
  REJEITADA: 'Rejeitada'
};

const fluxo = [
  { chave: 'SOLICITADA', label: 'Solicitada' },
  { chave: 'EM_APROVACAO', label: 'Em Aprovação' },
  { chave: 'APROVADA', label: 'Aprovada' },
  { chave: 'EM_COTACAO', label: 'Em Cotação' },
  { chave: 'COMPRA_REALIZADA', label: 'Compra Realizada' },
  { chave: 'EM_TRANSITO', label: 'Em Trânsito' },
  { chave: 'RECEBIDA', label: 'Recebida' },
  { chave: 'CONCLUIDA', label: 'Concluída' }
];

const dashboardVazio: DashboardAprovador = {
  pendentes_minha_alcada: 0,
  aprovadas_por_mim: 0,
  rejeitadas_por_mim: 0,
  tempo_medio_decisao_horas: 0,
  pendencias_proximas_data_necessidade: 0,
  pendencias_vencidas: 0
};

const dataCurta = (valor?: string) => {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleDateString('pt-PT');
};

const horaCurta = (valor?: string) => {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
};

const labelStatus = (status: string, labels: Record<string, string>) => labels[status] || status;

const urgencia = (valor?: string) => {
  if (!valor) return { label: 'Sem data', classe: 'muted' };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const data = new Date(valor);
  data.setHours(0, 0, 0, 0);
  const dias = Math.ceil((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

  if (dias < 0) return { label: 'Vencida', classe: 'status-blocked' };
  if (dias <= 3) return { label: 'Próxima', classe: 'status-warning' };
  return { label: 'No prazo', classe: 'muted' };
};

const progressoAtual = (req: RequisicaoAprovacao) => {
  if (req.status_aprovacao === 'REJEITADA') return 1;
  if (req.status === 'CONCLUIDO') return 7;
  if (['COMPRA_REALIZADA', 'EM_TRANSITO'].includes(req.status)) return 5;
  if (['PROPOSTA_SELECIONADA', 'AGUARDANDO_COMPROVATIVO'].includes(req.status)) return 4;
  if (req.status === 'EM_COTACAO') return 3;
  if (req.status_aprovacao === 'APROVADA') return 2;
  return 1;
};

const textoAcao = (acao: string) =>
  acao
    .replace('CRIAR_REQUISICAO', 'Requisição criada')
    .replace('REQUISICAO_APROVAR', 'Requisição aprovada')
    .replace('REQUISICAO_REJEITAR', 'Requisição rejeitada')
    .replace('FECHAR_COMPRA_DIRETA', 'Compra direta registada')
    .replace('SELECIONAR_PROPOSTA_VENCEDORA', 'Proposta vencedora selecionada')
    .replace('FECHAR_COMPRA_PROPOSTA', 'Compra com proposta registada')
    .replace('RECEBER_MERCADORIA', 'Mercadoria recebida')
    .replace(/_/g, ' ');

const comentarioAuditoria = (evento: HistoricoOperacional) => {
  if (!evento.valores_depois) return '';
  try {
    const valores = JSON.parse(evento.valores_depois);
    return valores.descricao || valores.observacao || valores.comentario || valores.observacoes || valores.titulo || valores.status_resultante || valores.status || '';
  } catch {
    return '';
  }
};

const Aprovacoes: React.FC = () => {
  const perfil = obterPerfilSessao();
  const podeDecidir = perfilPode(perfil, 'aprovacoes.decidir');
  const [aba, setAba] = useState<'pendentes' | 'decisoes'>('pendentes');
  const [pendentes, setPendentes] = useState<RequisicaoAprovacao[]>([]);
  const [decisoes, setDecisoes] = useState<DecisaoRealizada[]>([]);
  const [dashboard, setDashboard] = useState<DashboardAprovador>(dashboardVazio);
  const [detalhe, setDetalhe] = useState<DetalheAprovacao | null>(null);
  const [comentarioDecisao, setComentarioDecisao] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [acaoEmCurso, setAcaoEmCurso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [paginacaoPendentes, setPaginacaoPendentes] = useState<MetadadosPaginacao>(paginacaoVazia);
  const [paginacaoDecisoes, setPaginacaoDecisoes] = useState<MetadadosPaginacao>(paginacaoVazia);
  const [filtrosDecisoes, setFiltrosDecisoes] = useState({
    decisao: '',
    data_inicio: '',
    data_fim: '',
    departamento: '',
    pesquisa: ''
  });

  const carregarPendentes = async (pagina = paginacaoPendentes.pagina) => {
    const resposta = await api.get('/aprovacoes/pendentes', { params: { pagina, limite: paginacaoPendentes.limite } });
    setPendentes(resposta.data.dados || []);
    setPaginacaoPendentes(resposta.data.paginacao || paginacaoVazia);
  };

  const carregarDashboard = async () => {
    if (!podeDecidir) return;
    const resposta = await api.get('/aprovacoes/dashboard');
    setDashboard(resposta.data.dados || dashboardVazio);
  };

  const carregarDecisoes = async (pagina = paginacaoDecisoes.pagina) => {
    if (!podeDecidir) return;
    const parametros = Object.fromEntries(
      Object.entries(filtrosDecisoes).filter(([, valor]) => Boolean(valor))
    );
    const resposta = await api.get('/aprovacoes/minhas-decisoes', { params: { ...parametros, pagina, limite: paginacaoDecisoes.limite } });
    setDecisoes(resposta.data.dados || []);
    setPaginacaoDecisoes(resposta.data.paginacao || paginacaoVazia);
  };

  const recarregar = async () => {
    try {
      setCarregando(true);
      setErro(null);
      await Promise.all([
        carregarPendentes(paginacaoPendentes.pagina),
        ...(podeDecidir ? [carregarDashboard(), carregarDecisoes(paginacaoDecisoes.pagina)] : [])
      ]);
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar aprovações.'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirDetalhe = async (id: string) => {
    try {
      setCarregandoDetalhe(true);
      setErro(null);
      setComentarioDecisao('');
      const resposta = await api.get(`/aprovacoes/${id}/detalhe`);
      setDetalhe(resposta.data.dados);
    } catch (erroDetalhe: any) {
      setErro(extrairMensagemErro(erroDetalhe, 'Erro ao abrir detalhe da requisição.'));
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const decidir = async (acao: 'APROVAR' | 'REJEITAR') => {
    if (!detalhe?.requisicao || !podeDecidir || !detalhe.requisicao.pode_decidir) return;
    const comentario = comentarioDecisao.trim();

    if (acao === 'REJEITAR' && !comentario) {
      setErro('Informe uma justificativa para rejeitar a requisição.');
      return;
    }

    const confirmou = await dialog.confirm(acao === 'APROVAR' ? 'Confirmar aprovação?' : 'Confirmar rejeição?');
    if (!confirmou) return;

    try {
      setAcaoEmCurso(acao);
      setErro(null);
      setMensagem(null);
      const resposta = await api.post(`/aprovacoes/${detalhe.requisicao.id}/aprovar-ou-rejeitar`, {
        acao,
        comentarios: comentario
      });
      setMensagem(resposta.data.mensagem || 'Decisão registada.');
      setDetalhe(null);
      setComentarioDecisao('');
      await recarregar();
    } catch (erroDecisao: any) {
      setErro(extrairMensagemErro(erroDecisao, 'Erro ao processar aprovação.'));
    } finally {
      setAcaoEmCurso(null);
    }
  };

  const aplicarFiltrosDecisoes = async () => {
    try {
      setCarregando(true);
      setErro(null);
      await carregarDecisoes(1);
    } catch (erroFiltro: any) {
      setErro(extrairMensagemErro(erroFiltro, 'Erro ao filtrar decisões.'));
    } finally {
      setCarregando(false);
    }
  };

  const eventosDetalhe = useMemo(() => {
    if (!detalhe) return [];
    const req = detalhe.requisicao;
    return [
      {
        id: `${req.id}-criacao`,
        data: req.data_criacao,
        utilizador: req.criador?.nome || 'Solicitante',
        perfil: req.criador?.perfil || 'SOLICITANTE',
        acao: 'Requisição criada',
        comentario: req.descricao
      },
      ...detalhe.historico_aprovacoes.map((item) => ({
        id: item.id,
        data: item.data_decisao || item.data_criacao,
        utilizador: item.aprovador?.nome || 'Aprovador',
        perfil: item.aprovador?.perfil || 'APROVADOR',
        acao: item.status === 'APROVADA' ? 'Aprovação concedida' : 'Requisição rejeitada',
        comentario: item.comentarios || ''
      })),
      ...detalhe.historico_operacional.map((item) => ({
        id: item.id,
        data: item.data_criacao,
        utilizador: item.usuario?.nome || 'Sistema',
        perfil: item.usuario?.perfil || 'OPERACIONAL',
        acao: textoAcao(item.acao),
        comentario: comentarioAuditoria(item)
      }))
    ].sort((a, b) => new Date(a.data || 0).getTime() - new Date(b.data || 0).getTime());
  }, [detalhe]);

  const renderPendentes = () => (
    <section className="approval-responsive-list">
      {carregando && <div className="inline-loading"><div className="spinner"></div><span>Carregando aprovações...</span></div>}
      {!carregando && pendentes.map((item) => {
        const alertaUrgencia = urgencia(item.data_necessidade || item.data_vencimento);
        return (
          <article className={`approval-work-card ${alertaUrgencia.classe === 'status-blocked' ? 'urgent' : ''}`} key={item.id}>
            <div className="approval-card-main">
              <div className="approval-card-header">
                <span className="status-pill">{item.numero_requisicao}</span>
                <span className={`status-pill ${alertaUrgencia.classe}`}>{alertaUrgencia.label}</span>
                <span className="status-pill muted">{item.estado_operacional || 'Aguardando decisão'}</span>
              </div>
              <h2>{item.titulo}</h2>
              <dl className="meta-grid">
                <div><dt>Departamento/Centro de custo</dt><dd>{item.departamento || 'Nao informado'}</dd></div>
                <div><dt>Solicitante</dt><dd>{item.criador?.nome || 'Não informado'}</dd></div>
                <div><dt>Criação</dt><dd>{dataCurta(item.data_criacao)}</dd></div>
                <div><dt>Necessidade</dt><dd>{dataCurta(item.data_necessidade || item.data_vencimento)}</dd></div>
                <div><dt>Fluxo</dt><dd>{labelStatus(item.status, labelsStatusRequisicao)}</dd></div>
                <div><dt>Aprovação</dt><dd>{labelStatus(item.status_aprovacao, labelsStatusAprovacao)}</dd></div>
              </dl>
            </div>
            <div className="approval-card-actions">
              <button className="botao botao-secundario" type="button" onClick={() => abrirDetalhe(item.id)}>
                Ver detalhe
              </button>
              {podeDecidir && item.pode_decidir && (
                <button className="botao botao-primario" type="button" onClick={() => abrirDetalhe(item.id)}>
                  Decidir
                </button>
              )}
            </div>
          </article>
        );
      })}
      {!carregando && pendentes.length === 0 && <div className="empty-state">Não há aprovações pendentes.</div>}
      {!carregando && <Paginacao paginacao={paginacaoPendentes} carregando={carregando} aoMudarPagina={carregarPendentes} />}
    </section>
  );

  const renderDecisoes = () => (
    <section className="panel approval-history-panel">
      <div className="panel-heading">
        <h2>Minhas Decisões</h2>
        <button className="botao botao-secundario" type="button" onClick={aplicarFiltrosDecisoes}>Filtrar</button>
      </div>
      <div className="approval-filters-grid">
        <label>
          Pesquisa
          <input placeholder="Número ou título" value={filtrosDecisoes.pesquisa} onChange={(e) => setFiltrosDecisoes({ ...filtrosDecisoes, pesquisa: e.target.value })} />
        </label>
        <label>
          Decisão
          <select value={filtrosDecisoes.decisao} onChange={(e) => setFiltrosDecisoes({ ...filtrosDecisoes, decisao: e.target.value })}>
            <option value="">Todas</option>
            <option value="APROVADA">Aprovadas</option>
            <option value="REJEITADA">Rejeitadas</option>
          </select>
        </label>
        <label>
          Departamento
          <input value={filtrosDecisoes.departamento} onChange={(e) => setFiltrosDecisoes({ ...filtrosDecisoes, departamento: e.target.value })} />
        </label>
        <label>
          Início
          <input type="date" value={filtrosDecisoes.data_inicio} onChange={(e) => setFiltrosDecisoes({ ...filtrosDecisoes, data_inicio: e.target.value })} />
        </label>
        <label>
          Fim
          <input type="date" value={filtrosDecisoes.data_fim} onChange={(e) => setFiltrosDecisoes({ ...filtrosDecisoes, data_fim: e.target.value })} />
        </label>
      </div>

      {carregando ? (
        <div className="inline-loading"><div className="spinner"></div><span>Carregando decisões...</span></div>
      ) : (
        <div className="table-shell approval-history-table">
          <table>
            <thead>
              <tr>
                <th>Número</th>
                <th>Título</th>
                <th>Departamento</th>
                <th>Decisão</th>
                <th>Comentário</th>
                <th>Data/hora</th>
                <th>Estado atual</th>
              </tr>
            </thead>
            <tbody>
              {decisoes.map((item) => (
                <tr key={item.id}>
                  <td>{item.numero_requisicao}</td>
                  <td>{item.titulo}</td>
                  <td>{item.departamento || 'Nao informado'}</td>
                  <td><span className={`status-pill ${item.decisao === 'REJEITADA' ? 'status-blocked' : ''}`}>{labelStatus(item.decisao, labelsStatusAprovacao)}</span></td>
                  <td>{item.comentario || '-'}</td>
                  <td>{dataCurta(item.data_hora)} <small>{horaCurta(item.data_hora)}</small></td>
                  <td>{labelStatus(item.estado_atual_processo, labelsStatusRequisicao)}</td>
                </tr>
              ))}
              {decisoes.length === 0 && <tr><td colSpan={7}>Nenhuma decisão encontrada.</td></tr>}
            </tbody>
          </table>
          <Paginacao paginacao={paginacaoDecisoes} carregando={carregando} aoMudarPagina={carregarDecisoes} />
        </div>
      )}
    </section>
  );

  return (
    <div className="workspace-page aprovador-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Aprovador</span>
          <h1>Aprovações</h1>
          <p>{podeDecidir ? 'Validação da necessidade, urgência, itens e contexto operacional da requisição.' : 'Consulta gerencial das requisições que aguardam validação de necessidade.'}</p>
        </div>
        <button className="botao botao-secundario" type="button" onClick={recarregar}>Atualizar</button>
      </header>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {carregandoDetalhe && <div className="inline-loading"><div className="spinner"></div><span>Abrindo detalhe...</span></div>}

      {podeDecidir ? (
        <section className="metric-grid approver-metric-grid">
          <div className="metric-card"><span>Pendentes para decisão</span><strong>{dashboard.pendentes_decisao ?? dashboard.pendentes_minha_alcada}</strong></div>
          <div className="metric-card"><span>Aprovadas por mim</span><strong>{dashboard.aprovadas_por_mim}</strong></div>
          <div className="metric-card"><span>Rejeitadas por mim</span><strong>{dashboard.rejeitadas_por_mim}</strong></div>
          <div className="metric-card"><span>Tempo médio de decisão</span><strong>{dashboard.tempo_medio_decisao_horas}h</strong></div>
          <div className="metric-card"><span>Próximas da necessidade</span><strong>{dashboard.pendencias_proximas_data_necessidade}</strong><small>{dashboard.pendencias_vencidas} vencidas</small></div>
        </section>
      ) : (
        <section className="metric-grid approver-metric-grid">
          <div className="metric-card"><span>Pendentes para acompanhamento</span><strong>{pendentes.length}</strong></div>
        </section>
      )}

      {podeDecidir && (
        <div className="admin-tabs approval-tabs">
          <button className={aba === 'pendentes' ? 'active' : ''} type="button" onClick={() => setAba('pendentes')}>Aguardando decisão</button>
          <button className={aba === 'decisoes' ? 'active' : ''} type="button" onClick={() => setAba('decisoes')}>Minhas decisões</button>
        </div>
      )}

      {aba === 'pendentes' || !podeDecidir ? renderPendentes() : renderDecisoes()}

      {detalhe && (
        <div className="modal-overlay requisition-modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-conteudo requisition-detail-modal approval-decision-modal">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">{detalhe.requisicao.numero_requisicao}</span>
                <h2>{detalhe.requisicao.titulo}</h2>
              </div>
              <button className="botao botao-secundario" type="button" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>

            {detalhe.rejeicoes_anteriores.length > 0 && detalhe.rejeicoes_anteriores.map((rejeicao) => (
              <div className="rejection-box" key={rejeicao.id}>
                <strong>Rejeição anterior</strong>
                <span>{rejeicao.aprovador?.nome || 'Responsável não informado'} em {dataCurta(rejeicao.data_decisao || rejeicao.data_criacao)}</span>
                <p>{rejeicao.comentarios || 'Motivo não informado.'}</p>
              </div>
            ))}

            <section className="detail-section">
              <h3>Dados gerais</h3>
              <dl className="meta-grid">
                <div><dt>Número</dt><dd>{detalhe.requisicao.numero_requisicao}</dd></div>
                <div><dt>Solicitante</dt><dd>{detalhe.requisicao.criador?.nome || '-'}</dd></div>
                <div><dt>Departamento/Centro de custo</dt><dd>{detalhe.requisicao.departamento || 'Nao informado'}</dd></div>
                <div><dt>Criação</dt><dd>{dataCurta(detalhe.requisicao.data_criacao)}</dd></div>
                <div><dt>Necessidade</dt><dd>{dataCurta(detalhe.requisicao.data_necessidade || detalhe.requisicao.data_vencimento)}</dd></div>
                <div><dt>Fluxo</dt><dd>{labelStatus(detalhe.requisicao.status, labelsStatusRequisicao)}</dd></div>
                <div><dt>Aprovação</dt><dd>{labelStatus(detalhe.requisicao.status_aprovacao, labelsStatusAprovacao)}</dd></div>
              </dl>
              <p className="justification-text">{detalhe.requisicao.descricao}</p>
            </section>

            <section className="detail-section">
              <h3>Itens</h3>
              <div className="table-shell">
                <table>
                  <thead><tr><th>Item</th><th>Especificação</th><th>Quantidade</th><th>Unidade</th></tr></thead>
                  <tbody>
                    {(Array.isArray(detalhe.requisicao.itens) ? detalhe.requisicao.itens : []).map((item, indice) => (
                      <tr key={`${item.nome || 'item'}-${indice}`}>
                        <td>{item.nome || '-'}</td>
                        <td>{item.descricao_tecnica || item.especificacao || '-'}</td>
                        <td>{item.quantidade || '-'}</td>
                        <td>{item.unidade || '-'}</td>
                      </tr>
                    ))}
                    {(!Array.isArray(detalhe.requisicao.itens) || detalhe.requisicao.itens.length === 0) && (
                      <tr><td colSpan={4}>Itens estruturados não registados nesta requisição.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="detail-section">
              <h3>Timeline</h3>
              <div className={`timeline-progress ${detalhe.requisicao.status_aprovacao === 'REJEITADA' ? 'rejected' : ''}`}>
                {fluxo.map((etapa, indice) => (
                  <div className={`timeline-step ${indice <= progressoAtual(detalhe.requisicao) ? 'active' : ''}`} key={etapa.chave}>
                    <span>{indice + 1}</span>
                    <strong>{etapa.label}</strong>
                  </div>
                ))}
              </div>
            </section>

            <section className="detail-section">
              <h3>Histórico cronológico</h3>
              <div className="history-list">
                {eventosDetalhe.map((evento) => (
                  <div className="history-item" key={evento.id}>
                    <time>{dataCurta(evento.data)}<span>{horaCurta(evento.data)}</span></time>
                    <div>
                      <strong>{evento.acao}</strong>
                      <small>{evento.utilizador} · {evento.perfil}</small>
                      {evento.comentario && <p>{evento.comentario}</p>}
                    </div>
                  </div>
                ))}
                {eventosDetalhe.length === 0 && <div className="empty-state">Ainda não há eventos registados.</div>}
              </div>
            </section>

            {podeDecidir && detalhe.requisicao.pode_decidir && (
              <section className="decision-box approval-modal-decision-box">
                <label>
                  Comentário da decisão
                  <textarea
                    placeholder="Opcional para aprovação, obrigatório para rejeição"
                    value={comentarioDecisao}
                    onChange={(e) => setComentarioDecisao(e.target.value)}
                  />
                </label>
                <div className="button-row">
                  <button className="botao botao-secundario" type="button" disabled={Boolean(acaoEmCurso)} onClick={() => decidir('REJEITAR')}>
                    {acaoEmCurso === 'REJEITAR' ? 'Rejeitando...' : 'Rejeitar'}
                  </button>
                  <button className="botao botao-primario" type="button" disabled={Boolean(acaoEmCurso)} onClick={() => decidir('APROVAR')}>
                    {acaoEmCurso === 'APROVAR' ? 'Aprovando...' : 'Aprovar'}
                  </button>
                </div>
              </section>
            )}

            {(!podeDecidir || !detalhe.requisicao.pode_decidir) && (
              <div className="alerta alerta-info">{detalhe.requisicao.estado_operacional || 'Decisão indisponível para esta requisição.'}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Aprovacoes;
